import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, GitCompare, Loader2, TrendingUp, TrendingDown, Minus,
  AlertTriangle, CheckCircle2, FileText, Sparkles,
} from "lucide-react";
import api from "@/lib/api";

const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function Compare() {
  const { projectId } = useParams();
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const navigate = useNavigate();

  const [estimations, setEstimations] = useState([]);
  const [estA, setEstA] = useState("");
  const [estB, setEstB] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/estimations`)
      .then(r => {
        const list = r.data || [];
        setEstimations(list);
        if (list.length >= 2) {
          setEstA(list[1].id); // second oldest = "rev 1"
          setEstB(list[0].id); // newest = "rev 2"
        } else if (list.length === 1) {
          setEstA(list[0].id);
        }
      })
      .catch(e => setError(e?.response?.data?.detail || "Error"));
  }, [projectId]);

  const run = async () => {
    if (!estA || !estB || estA === estB) {
      setError(es ? "Selecciona dos revisiones diferentes" : "Pick two different revisions");
      return;
    }
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await api.post("/compare/estimations", {
        estimation_a_id: estA, estimation_b_id: estB,
        language: es ? "es" : "en",
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="compare-page">
      <header className="flex items-start justify-between mb-6">
        <div>
          <Link to={`/projects/${projectId}`}
            className="text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-[#003366] inline-flex items-center gap-1 mb-2"
            data-testid="compare-back-link">
            <ArrowLeft size={12} /> {es ? "Volver al proyecto" : "Back to project"}
          </Link>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#003366]">
            <GitCompare size={13} /> {es ? "COMPARAR REVISIONES" : "COMPARE REVISIONS"}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">
            {es ? "Rev 1 vs Rev 2" : "Rev 1 vs Rev 2"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {es
              ? "Detecta cambios de BOM, costos y riesgos entre dos versiones del plano. Analiza con IA."
              : "Detect BOM, cost & risk changes between two plan revisions, with AI narrative."}
          </p>
        </div>
      </header>

      <div className="iqes-card p-5 mb-6">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
              {es ? "Revisión A (anterior)" : "Revision A (older)"}
            </label>
            <select data-testid="compare-est-a"
              value={estA} onChange={e => setEstA(e.target.value)}
              className="iqes-input">
              <option value="">—</option>
              {estimations.map(e => (
                <option key={e.id} value={e.id}>
                  {e.file_name} · {new Date(e.created_at).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
              {es ? "Revisión B (nueva)" : "Revision B (newer)"}
            </label>
            <select data-testid="compare-est-b"
              value={estB} onChange={e => setEstB(e.target.value)}
              className="iqes-input">
              <option value="">—</option>
              {estimations.map(e => (
                <option key={e.id} value={e.id}>
                  {e.file_name} · {new Date(e.created_at).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          data-testid="compare-run-btn"
          onClick={run} disabled={loading || !estA || !estB}
          className="bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2 shadow-sm"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {loading
            ? (es ? "Analizando con IA…" : "Running AI analysis…")
            : (es ? "Comparar" : "Compare")}
        </button>
        {estimations.length < 2 && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            {es ? "Necesitas al menos 2 estimaciones en este proyecto." : "You need at least 2 estimations in this project."}
          </div>
        )}
        {error && <div data-testid="compare-error" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>}
      </div>

      {result && (
        <div className="space-y-6" data-testid="compare-result">
          {/* Totals delta strip */}
          <div className="grid md:grid-cols-3 gap-4">
            <DeltaCard label={es ? "Materiales" : "Materials"}
              oldVal={result.diff.totals.materials_old}
              newVal={result.diff.totals.materials_new}
              delta={result.diff.totals.materials_delta} />
            <DeltaCard label={es ? "Mano de obra" : "Labor"}
              oldVal={result.diff.totals.labor_old}
              newVal={result.diff.totals.labor_new}
              delta={result.diff.totals.labor_delta} />
            <DeltaCard label={es ? "Total" : "Grand total"}
              oldVal={result.diff.totals.grand_old}
              newVal={result.diff.totals.grand_new}
              delta={result.diff.totals.grand_delta}
              pct={result.diff.totals.grand_pct}
              highlight />
          </div>

          {/* AI Narrative */}
          <div className="iqes-card p-5" data-testid="compare-narrative">
            <div className="text-xs font-mono uppercase tracking-wider text-[#003366] mb-2 flex items-center gap-2">
              <Sparkles size={13} /> {es ? "Análisis IA — Claude Sonnet 4.5" : "AI Analysis — Claude Sonnet 4.5"}
            </div>
            <p className="text-sm text-slate-800 leading-relaxed mb-4">{result.narrative.executive_summary}</p>

            {result.narrative.key_changes?.length > 0 && (
              <>
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  {es ? "Cambios clave" : "Key changes"}
                </div>
                <ul className="space-y-1.5 mb-4">
                  {result.narrative.key_changes.map((k, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <span className="text-[#EA580C]">▸</span>{k}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {result.narrative.risk_flags?.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                  {es ? "Banderas de riesgo" : "Risk flags"}
                </div>
                <div className="space-y-2">
                  {result.narrative.risk_flags.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <AlertTriangle size={14} className={`mt-0.5 ${
                        r.severity === "high" ? "text-red-600" :
                        r.severity === "medium" ? "text-amber-600" : "text-slate-500"
                      }`} />
                      <div>
                        <span className={`text-[10px] font-mono uppercase mr-2 px-1.5 py-0.5 rounded ${
                          r.severity === "high" ? "bg-red-50 text-red-700" :
                          r.severity === "medium" ? "bg-amber-50 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>{r.severity}</span>
                        <b className="text-slate-800">{r.item}</b> — <span className="text-slate-600">{r.note}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.narrative.recommendation && (
              <div className="mt-4 pt-4 border-t border-slate-100 bg-[#003366]/5 -mx-5 -mb-5 px-5 py-3 rounded-b-2xl">
                <div className="text-[11px] font-mono uppercase tracking-wider text-[#003366] mb-1 flex items-center gap-1">
                  <CheckCircle2 size={12} /> {es ? "Recomendación" : "Recommendation"}
                </div>
                <p className="text-sm text-slate-800">{result.narrative.recommendation}</p>
              </div>
            )}
          </div>

          {/* Structural diff */}
          <div className="grid md:grid-cols-3 gap-4">
            <DiffList title={es ? "Añadidos" : "Added"} items={result.diff.added} tone="green" testid="compare-added" />
            <DiffList title={es ? "Removidos" : "Removed"} items={result.diff.removed} tone="red" testid="compare-removed" />
            <DiffList title={es ? "Cambiados" : "Changed"} items={result.diff.changed} tone="amber" testid="compare-changed" isChanged />
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaCard({ label, oldVal, newVal, delta, pct, highlight }) {
  const up = delta > 0;
  const Icon = delta === 0 ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <div className={`iqes-card p-4 ${highlight ? "border-[#003366] bg-[#003366]/5" : ""}`}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-1.5 mb-0.5">
        <span className="text-2xl font-mono font-semibold text-slate-900">{fmt.format(newVal)}</span>
        {pct != null && <span className={`text-xs font-mono ${up ? "text-red-600" : delta < 0 ? "text-green-600" : "text-slate-500"}`}>
          {up ? "+" : ""}{pct}%
        </span>}
      </div>
      <div className="flex items-center gap-1 text-xs font-mono">
        <span className="text-slate-400">{fmt.format(oldVal)}</span>
        <Icon size={11} className={delta === 0 ? "text-slate-400" : up ? "text-red-600" : "text-green-600"} />
        <span className={delta === 0 ? "text-slate-500" : up ? "text-red-600" : "text-green-600"}>
          {up ? "+" : ""}{fmt.format(delta)}
        </span>
      </div>
    </div>
  );
}

function DiffList({ title, items, tone, testid, isChanged }) {
  const toneMap = {
    green: "bg-green-50 border-green-200",
    red: "bg-red-50 border-red-200",
    amber: "bg-amber-50 border-amber-200",
  };
  return (
    <div className={`iqes-card overflow-hidden`} data-testid={testid}>
      <div className={`px-4 py-2.5 border-b ${toneMap[tone]} border-b-slate-200`}>
        <div className="text-xs font-mono uppercase tracking-wider text-slate-700 flex justify-between">
          <span>{title}</span>
          <span className="font-semibold">{items.length}</span>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-4 text-xs text-slate-400 italic text-center">—</div>
        ) : (
          items.map((it, i) => (
            <div key={i} className="px-4 py-2.5 border-b border-slate-100 text-sm">
              <div className="font-medium text-slate-900 truncate">{it.label}</div>
              {isChanged ? (
                <div className="text-xs font-mono text-slate-600 flex justify-between mt-0.5">
                  <span>×{it.qty_old} → ×{it.qty_new} ({it.qty_delta > 0 ? "+" : ""}{it.qty_delta})</span>
                  <span className={it.total_delta > 0 ? "text-red-600" : "text-green-600"}>
                    {it.total_delta > 0 ? "+" : ""}{fmt.format(it.total_delta)}
                  </span>
                </div>
              ) : (
                <div className="text-xs font-mono text-slate-600 flex justify-between mt-0.5">
                  <span>×{it.qty} {it.category}</span>
                  <span className="text-slate-700">{fmt.format(it.total)}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
